import { useThemeEditorStore } from '@/features/theme-editor/stores/themeEditorStore';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { ImageFrames } from '@/types/store';
import { delay } from 'lodash';
import { useEffect, useMemo } from 'react';
import { FontCollection } from '../constants/themeConstants';
import { useCustomizationStore } from '../stores/customizationStore';
import { Fonts } from '../types/store';
import { Menu, MenuRoute, Routes, useMenuNavigation } from './Menu';
import { Icon } from './ui/Icon';
import { AddBlock } from './views/blocks/AddBlock';
import { EditorialText } from './views/blocks/EditorialText';
import { Gallery } from './views/blocks/Gallery';
import { Header } from './views/blocks/Header';
import { ProductLayout } from './views/blocks/ProductLayout';
import { Testimonials } from './views/blocks/Testimonials';
import { Title } from './views/blocks/Title';
import { ColorsView } from './views/customizations/Colors';
import { Themes as ThemesView } from './views/customizations/Themes';
export { Routes };

export const CustomizationMenu = () => {
    const isMobile = useIsMobile();
    const { primaryFont, imageFrames, updateStoreCustomizations, clearPendingItems } = useCustomizationStore();
    const { menuOpen, setEditBlockId, editBlockId, errorBlockId, setMenuOpen } = useThemeEditorStore();
    const { reset } = useMenuNavigation();

    useEffect(() => {
        if (!menuOpen) {
            // Reset if has been in edit mode and menu is closed. Add a small delay to ensure the menu is closed.
            delay(() => {
                reset();
                setEditBlockId(null);
            }, 300);
        }
    }, [menuOpen, setEditBlockId, reset]);

    useEffect(() => {
        // If there is an edit block id or error block id, set the menu open.
        if (editBlockId || errorBlockId) setMenuOpen(true);
    }, [errorBlockId, editBlockId]);

    useEffect(() => {
        // Sync menu visibility with breakpoint: desktop = menu open, mobile = menu closed.
        // useIsMobile uses matchMedia which only changes when width crosses 768px, so Android keyboard (height change) doesn't close the menu.
        setMenuOpen(!isMobile);
    }, [isMobile, setMenuOpen]);

    const handleNavigate = useMemo(
        () => (path: string[]) => {
            if (path.length === 0) {
                setEditBlockId(null);
                clearPendingItems();
            }
        },
        [setEditBlockId, clearPendingItems]
    );

    // Define all routes in a flat array
    const routes: MenuRoute[] = useMemo(() => {
        const allRoutes: MenuRoute[] = [
            // Root level routes
            {
                id: Routes.THEMES,
                path: [Routes.THEMES],
                label: 'Themes',
                icon: <Icon name='theme' />,
                description: 'Choose a style foundation that sets the tone for your store.',
                view: <ThemesView />,
            },
            {
                id: Routes.COLORS,
                path: [Routes.COLORS],
                label: 'Colors',
                icon: <Icon name='color' />,
                description: "Pick a palette that matches your brand's personality.",
                view: <ColorsView />,
            },
            {
                id: Routes.FONTS,
                path: [Routes.FONTS],
                label: 'Fonts',
                icon: <Icon name='font' />,
                description: 'Select typefaces that give your words their unique voice.',
            },
            {
                id: Routes.IMAGE_FRAMES,
                path: [Routes.IMAGE_FRAMES],
                label: 'Image frames',
                icon: <Icon name='image-frame' />,
                description: 'Style your photos with unique shapes and creative masks.',
            },

            // Fonts submenu routes
            {
                id: Routes.FONTS_BEBAS_NEUE,
                path: [Routes.FONTS, Routes.FONTS_BEBAS_NEUE],
                label: 'Bebas Neue',
                icon: <Icon name='bebas-neue' />,
                description: 'Tall, bold, condensed. Structured and strong.',
                active: primaryFont === Fonts.BEBAS_NEUE,
                onClick: () =>
                    updateStoreCustomizations({
                        fonts: {
                            primary: FontCollection[Fonts.BEBAS_NEUE].primary,
                            secondary: FontCollection[Fonts.BEBAS_NEUE].secondary,
                        },
                    }),
            },
            {
                id: Routes.FONTS_SPICY_RICE,
                path: [Routes.FONTS, Routes.FONTS_SPICY_RICE],
                label: 'Spicy Rice',
                icon: <Icon name='spicy-rice' />,
                description: 'Quirky, vibrant, playful drama.',
                active: primaryFont === Fonts.SPICY_RICE,
                onClick: () =>
                    updateStoreCustomizations({
                        fonts: {
                            primary: FontCollection[Fonts.SPICY_RICE].primary,
                            secondary: FontCollection[Fonts.SPICY_RICE].secondary,
                        },
                    }),
            },
            {
                id: Routes.FONTS_LORA,
                path: [Routes.FONTS, Routes.FONTS_LORA],
                label: 'Lora',
                icon: <Icon name='lora' />,
                description: 'Elegant serif. Balanced and warm.',
                active: primaryFont === Fonts.LORA,
                onClick: () =>
                    updateStoreCustomizations({
                        fonts: {
                            primary: FontCollection[Fonts.LORA].primary,
                            secondary: FontCollection[Fonts.LORA].secondary,
                        },
                    }),
            },
            {
                id: Routes.FONTS_PLAYFAIR_DISPLAY,
                path: [Routes.FONTS, Routes.FONTS_PLAYFAIR_DISPLAY],
                label: 'Playfair Display',
                icon: <Icon name='playfair-display' />,
                description: 'High-contrast serif. Dramatic and refined.',
                active: primaryFont === Fonts.PLAYFAIR_DISPLAY,
                onClick: () =>
                    updateStoreCustomizations({
                        fonts: {
                            primary: FontCollection[Fonts.PLAYFAIR_DISPLAY].primary,
                            secondary: FontCollection[Fonts.PLAYFAIR_DISPLAY].secondary,
                        },
                    }),
            },
            {
                id: Routes.FONTS_FJALLA_ONE,
                path: [Routes.FONTS, Routes.FONTS_FJALLA_ONE],
                label: 'Fjalla One',
                icon: <Icon name='fjalla-one' />,
                description: 'Clear, impactful. Clean lines, bold rhythm.',
                active: primaryFont === Fonts.FJALLA_ONE,
                onClick: () =>
                    updateStoreCustomizations({
                        fonts: {
                            primary: FontCollection[Fonts.FJALLA_ONE].primary,
                            secondary: FontCollection[Fonts.FJALLA_ONE].secondary,
                        },
                    }),
            },
            {
                id: Routes.FONTS_BRICOLAGE_GROTESQUE,
                path: [Routes.FONTS, Routes.FONTS_BRICOLAGE_GROTESQUE],
                label: 'Bricolage Grotesque',
                icon: <Icon name='bricolage-grotesque' />,
                description: 'Quirky cuts. Editorial and stylish.',
                active: primaryFont === Fonts.BRIOLAGE_GROTESQUE,
                onClick: () =>
                    updateStoreCustomizations({
                        fonts: {
                            primary: FontCollection[Fonts.BRIOLAGE_GROTESQUE].primary,
                            secondary: FontCollection[Fonts.BRIOLAGE_GROTESQUE].secondary,
                        },
                    }),
            },

            // Image frames submenu routes
            {
                id: Routes.IMAGE_FRAMES_ARC,
                path: [Routes.IMAGE_FRAMES, Routes.IMAGE_FRAMES_ARC],
                label: 'Arc',
                icon: <Icon name='arc' />,
                description: 'Soft top curve for a smooth look.',
                active: imageFrames === ImageFrames.ARC,
                onClick: () => updateStoreCustomizations({ imageFrames: ImageFrames.ARC }),
            },
            {
                id: Routes.IMAGE_FRAMES_SQUARE,
                path: [Routes.IMAGE_FRAMES, Routes.IMAGE_FRAMES_SQUARE],
                label: 'Square',
                icon: <Icon name='square' />,
                description: 'Sharp corners for a clean style.',
                active: imageFrames === ImageFrames.SQUARE,
                onClick: () => updateStoreCustomizations({ imageFrames: ImageFrames.SQUARE }),
            },
            {
                id: Routes.IMAGE_FRAMES_ROUNDED_LOW,
                path: [Routes.IMAGE_FRAMES, Routes.IMAGE_FRAMES_ROUNDED_LOW],
                label: 'Rounded Low',
                icon: <Icon name='rounded-low' />,
                description: 'Subtle curves with a light feel.',
                active: imageFrames === ImageFrames.ROUNDED_LOW,
                onClick: () => updateStoreCustomizations({ imageFrames: ImageFrames.ROUNDED_LOW }),
            },
            {
                id: Routes.IMAGE_FRAMES_ROUNDED_MEDIUM,
                path: [Routes.IMAGE_FRAMES, Routes.IMAGE_FRAMES_ROUNDED_MEDIUM],
                label: 'Rounded Medium',
                icon: <Icon name='rounded-medium' />,
                description: 'Even curves for a modern look.',
                active: imageFrames === ImageFrames.ROUNDED_MEDIUM,
                onClick: () => updateStoreCustomizations({ imageFrames: ImageFrames.ROUNDED_MEDIUM }),
            },
            {
                id: Routes.IMAGE_FRAMES_ROUNDED_STRONG,
                path: [Routes.IMAGE_FRAMES, Routes.IMAGE_FRAMES_ROUNDED_STRONG],
                label: 'Rounded Strong',
                icon: <Icon name='rounded-strong' />,
                description: 'Bold curves for a warm feel.',
                active: imageFrames === ImageFrames.ROUNDED_STRONG,
                onClick: () => updateStoreCustomizations({ imageFrames: ImageFrames.ROUNDED_STRONG }),
            },
            {
                id: Routes.IMAGE_FRAMES_FLORAL,
                path: [Routes.IMAGE_FRAMES, Routes.IMAGE_FRAMES_FLORAL],
                label: 'Floral',
                icon: <Icon name='floral' />,
                description: 'Petal-like edges for decoration.',
                active: imageFrames === ImageFrames.FLORAL,
                onClick: () => updateStoreCustomizations({ imageFrames: ImageFrames.FLORAL }),
            },

            // Programmatic routes (not shown in menu, navigated to via navigateById)
            {
                id: Routes.ADD_BLOCK,
                path: [],
                label: 'New Block',
                view: (id?: number) => <AddBlock id={id ?? 0} />,
                hidden: true,
            },
            {
                id: Routes.ADD_BLOCK_TITLE,
                path: [],
                label: 'Title',
                view: (id?: number) => <Title id={id ?? 0} />,
                hidden: true,
            },
            {
                id: Routes.ADD_BLOCK_EDITORIAL_TEXT,
                path: [],
                label: 'Editorial Text',
                view: (id?: number) => <EditorialText id={id ?? 0} />,
                hidden: true,
            },
            {
                id: Routes.ADD_BLOCK_GALLERY,
                path: [],
                label: 'Gallery',
                view: (id?: number) => <Gallery id={id ?? 0} />,
                hidden: true,
            },
            {
                id: Routes.ADD_BLOCK_TESTIMONIALS,
                path: [],
                label: 'Testimonials',
                view: (id?: number) => <Testimonials id={id ?? 0} />,
                hidden: true,
            },
            {
                id: Routes.EDIT_BLOCK_TITLE,
                path: [],
                label: 'Edit title',
                view: (id?: number) => <Title id={id ?? 0} />,
                hidden: true,
            },
            {
                id: Routes.EDIT_BLOCK_EDITORIAL_TEXT,
                path: [],
                label: 'Edit editorial text',
                view: (id?: number) => <EditorialText id={id ?? 0} />,
                hidden: true,
            },
            {
                id: Routes.EDIT_BLOCK_GALLERY,
                path: [],
                label: 'Edit gallery',
                view: (id?: number) => <Gallery id={id ?? 0} />,
                hidden: true,
            },
            {
                id: Routes.EDIT_BLOCK_TESTIMONIALS,
                path: [],
                label: 'Edit testimonials',
                view: (id?: number) => <Testimonials id={id ?? 0} />,
                hidden: true,
            },
            {
                id: Routes.EDIT_BLOCK_HEADER,
                path: [],
                label: 'Edit header',
                view: () => <Header />,
                hidden: true,
            },
            {
                id: Routes.EDIT_BLOCK_PRODUCT_LAYOUT,
                path: [],
                label: 'Edit product',
                view: (id?: number) => <ProductLayout id={id ?? 0} />,
                hidden: true,
            },
        ];

        return allRoutes;
    }, [primaryFont, imageFrames, updateStoreCustomizations]);

    return <Menu routes={routes} onNavigate={handleNavigate} onClose={() => setMenuOpen(false)} />;
};
